/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { PlanId } from "components/common/boundary/PlanId";
import { minimumPeriod, Period } from "hooks/periodIndex";

import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";

import type { RootState } from "store/store";

interface FinishedForm {
  type: "finished_form";
  planId: PlanId;
}

interface MessageForm {
  type: "message_form";
  planId: PlanId;
  periodMessage: Record<Period, string>;
  backupPlanMessage: Record<PlanId, string>;
}

type State = "static" | "submitting";

interface DeployForm {
  type: "deploy_form";
  planId: PlanId;
  preview: {
    planId: PlanId;
    period: Period;
    title: string;
    isBackup: boolean;
  } | null;
  selectedPeriods: Period[];
  selectedBackups: PlanId[];
  manualHeatOverrides: Record<Period, Record<number, string>>;
  state: State;
}

const deployPlanFormAdapter = createEntityAdapter({
  selectId: (form: DeployForm | MessageForm | FinishedForm) => form.planId,
});

const deployPlanFormSelectors = deployPlanFormAdapter.getSelectors();

export const deployPlanFormSlice = createSlice({
  name: "deployPlanForm",
  initialState: deployPlanFormAdapter.getInitialState(undefined, []),
  reducers: {
    cancelFlow(state, { payload: planId }: PayloadAction<PlanId>) {
      deployPlanFormAdapter.removeOne(state, planId);
    },
    setDeployForm(
      state,
      {
        payload: { planId },
      }: PayloadAction<{
        planId: PlanId;
      }>
    ) {
      deployPlanFormAdapter.addOne(state, {
        planId,
        preview: null,
        selectedBackups: [],
        selectedPeriods: [],
        manualHeatOverrides: {},
        type: "deploy_form",
        state: "static",
      });
    },
    setMessageForm(
      state,
      {
        payload: [planId, periodMessages, backupMessages],
      }: PayloadAction<
        [
          PlanId,
          {
            period: Period;
            message: string;
          }[],
          {
            planId: PlanId;
            message: string;
          }[],
        ]
      >
    ) {
      deployPlanFormAdapter.setOne(state, {
        planId,
        type: "message_form",
        periodMessage: typeSafeObjectFromEntries(
          periodMessages.map(({ period, message }) => [period, message])
        ),
        backupPlanMessage: typeSafeObjectFromEntries(
          backupMessages.map(({ planId, message }) => [planId, message])
        ),
      });
    },
    setFinishedForm(state, { payload }: PayloadAction<PlanId>) {
      deployPlanFormAdapter.setOne(state, {
        type: "finished_form",
        planId: payload,
      });
    },
    setPeriodMessage(
      state,
      {
        payload: [planId, message, period],
      }: PayloadAction<[PlanId, string, Period]>
    ) {
      const form = deployPlanFormSelectors.selectById(state, planId);
      if (form?.type === "message_form") {
        deployPlanFormAdapter.setOne(state, {
          ...form,
          periodMessage: {
            ...form.periodMessage,
            [period]: message,
          },
        });
      }
    },
    setSMSBackupMessage(
      state,
      {
        payload: [formPlanId, message, backupPlanId],
      }: PayloadAction<[PlanId, string, PlanId]>
    ) {
      const form = deployPlanFormSelectors.selectById(state, formPlanId);
      if (form?.type === "message_form") {
        deployPlanFormAdapter.setOne(state, {
          ...form,
          backupPlanMessage: {
            ...form.backupPlanMessage,
            [backupPlanId]: message,
          },
        });
      }
    },
    clear(state, { payload: planId }: PayloadAction<PlanId>) {
      deployPlanFormAdapter.removeOne(state, planId);
    },
    setPreview(
      state,
      {
        payload: [planId, preview],
      }: PayloadAction<
        [
          PlanId,
          { planId: PlanId; period: Period; title: string; isBackup: boolean },
        ]
      >
    ) {
      const deployPlanForm = deployPlanFormSelectors.selectById(state, planId);
      if (deployPlanForm?.type === "deploy_form") {
        deployPlanFormAdapter.updateOne(state, {
          id: planId,
          changes: { preview },
        });
      }
    },
    addAll(
      state,
      {
        payload: [planId, backupPlanIds, periods],
      }: PayloadAction<[PlanId, PlanId[], Period[]]>
    ) {
      const deployPlanForm = deployPlanFormSelectors.selectById(state, planId);
      if (deployPlanForm?.type === "deploy_form") {
        deployPlanFormAdapter.updateOne(state, {
          id: planId,
          changes: {
            selectedPeriods: [
              ...deployPlanForm.selectedPeriods.filter(
                (period) => !periods.includes(period)
              ),
              ...periods,
            ],
            selectedBackups: [
              ...deployPlanForm.selectedBackups.filter(
                (planId) => !backupPlanIds.includes(planId)
              ),
              ...backupPlanIds,
            ],
          },
        });
      }
    },
    addPeriod(
      state,
      {
        payload: { planId, period },
      }: PayloadAction<{ planId: PlanId; period: Period }>
    ) {
      const deployPlanForm = deployPlanFormSelectors.selectById(state, planId);
      if (deployPlanForm?.type === "deploy_form") {
        deployPlanFormAdapter.updateOne(state, {
          id: planId,
          changes: {
            ...deployPlanForm,
            selectedPeriods: [
              ...deployPlanForm.selectedPeriods.filter(
                (selectedPeriod) => selectedPeriod !== period
              ),
              period,
            ],
          },
        });
      }
    },
    removePeriod(
      state,
      {
        payload: { planId, period },
      }: PayloadAction<{ planId: PlanId; period: Period }>
    ) {
      const deployPlanForm = deployPlanFormSelectors.selectById(state, planId);
      if (deployPlanForm?.type === "deploy_form") {
        deployPlanFormAdapter.updateOne(state, {
          id: planId,
          changes: {
            ...deployPlanForm,
            selectedPeriods: deployPlanForm.selectedPeriods.filter(
              (deployedPeriod) => deployedPeriod !== period
            ),
          },
        });
      }
    },
    addBackup(
      state,
      {
        payload: [planId, backup],
      }: PayloadAction<[PlanId, { planId: PlanId; period: Period }]>
    ) {
      const deployPlanForm = deployPlanFormSelectors.selectById(state, planId);
      if (deployPlanForm?.type === "deploy_form") {
        deployPlanFormAdapter.updateOne(state, {
          id: planId,
          changes: {
            ...deployPlanForm,
            selectedBackups: [
              ...deployPlanForm.selectedBackups.filter(
                (planId) => backup.planId !== planId
              ),
              backup.planId,
            ],
          },
        });
      }
    },
    removeBackup(
      state,
      {
        payload: [planId, backup],
      }: PayloadAction<[PlanId, { planId: PlanId; period: Period }]>
    ) {
      const deployPlanForm = deployPlanFormSelectors.selectById(state, planId);
      if (deployPlanForm?.type === "deploy_form") {
        deployPlanFormAdapter.updateOne(state, {
          id: planId,
          changes: {
            ...deployPlanForm,
            selectedBackups: deployPlanForm.selectedBackups.filter(
              (planId) => planId !== backup.planId
            ),
          },
        });
      }
    },
    setMixOverride(
      state,
      {
        payload: { planId, period, mixId, override },
      }: PayloadAction<{
        planId: PlanId;
        period: Period;
        mixId: number;
        override: string;
      }>
    ) {
      const deployPlanForm = deployPlanFormSelectors.selectById(state, planId);
      if (deployPlanForm?.type === "deploy_form") {
        const existing = deployPlanForm.manualHeatOverrides[period] ?? {};
        const periodOverrides: Record<number, string> =
          override === ""
            ? Object.fromEntries(
                Object.entries(existing).filter(
                  ([key]) => Number(key) !== mixId
                )
              )
            : { ...existing, [mixId]: override };
        deployPlanFormAdapter.updateOne(state, {
          id: planId,
          changes: {
            manualHeatOverrides: {
              ...deployPlanForm.manualHeatOverrides,
              [period]: periodOverrides,
            },
          },
        });
      }
    },
    submittingDeployForm(state, { payload }: PayloadAction<PlanId>) {
      const deployPlanForm = deployPlanFormSelectors.selectById(state, payload);
      if (deployPlanForm) {
        switch (deployPlanForm.type) {
          case "deploy_form": {
            deployPlanFormAdapter.updateOne(state, {
              id: payload,
              changes: { state: "submitting" },
            });
          }
        }
      }
    },
    resetDeployFormSubmission(state, { payload }: PayloadAction<PlanId>) {
      const deployPlanForm = deployPlanFormSelectors.selectById(state, payload);
      if (deployPlanForm?.type === "deploy_form") {
        deployPlanFormAdapter.updateOne(state, {
          id: payload,
          changes: { state: "static" },
        });
      }
    },
    backToDeployForm(state, { payload }: PayloadAction<PlanId>) {
      const deployPlanForm = deployPlanFormSelectors.selectById(state, payload);
      if (deployPlanForm) {
        switch (deployPlanForm.type) {
          case "message_form": {
            deployPlanFormAdapter.setOne(state, {
              state: "static",
              type: "deploy_form",
              planId: payload,
              preview: null,
              selectedBackups: Object.keys(
                deployPlanForm.backupPlanMessage
              ).map((key) => Number(key) as PlanId),
              selectedPeriods: Object.keys(deployPlanForm.periodMessage).map(
                (key) => Number(key) as Period
              ),
              manualHeatOverrides: {},
            });
          }
        }
      }
    },
  },
});

const createAppSelector = createSelector.withTypes<RootState>();

export const selectDeployPlanForm = createAppSelector(
  [(state) => state.deployPlanForm, (_, planId: PlanId) => planId],
  (deployPlanForm, planId) =>
    deployPlanFormSelectors.selectById(deployPlanForm, planId)
);

export const selectDeployPlanFormType = createAppSelector(
  [selectDeployPlanForm],
  (form) => form?.type
);

export const selectDeployForm = createAppSelector(
  [selectDeployPlanForm],
  (form) => {
    if (form?.type === "deploy_form") {
      return form;
    }
  }
);

export const selectMessagePlanForm = createAppSelector(
  [selectDeployPlanForm],
  (form) => {
    if (form?.type === "message_form") {
      return form;
    }
  }
);

export const selectPlanFormPeriodMessage = createAppSelector(
  [selectMessagePlanForm, (_, __, previewPeriod: Period) => previewPeriod],
  (deployForm, period) => {
    if (deployForm) {
      return deployForm.periodMessage[period];
    }
  }
);

export const selectPlanFormBackupMessage = createAppSelector(
  [selectMessagePlanForm, (_, __, backupPlanId: PlanId) => backupPlanId],
  (smsDeployForm, backupPlanId) => {
    if (smsDeployForm) {
      return smsDeployForm.backupPlanMessage[backupPlanId];
    }
  }
);

export const selectDeployPlanPeriodSelected = createAppSelector(
  [selectDeployForm, (_, __, period: Period) => period],
  (deployPlanForm, period) => {
    if (deployPlanForm) {
      return deployPlanForm.selectedPeriods.includes(period);
    } else {
      return false;
    }
  }
);

export const selectDeployBackupPlanSelected = createAppSelector(
  [selectDeployForm, (_, __, backupPlanId: PlanId) => backupPlanId],
  (deployPlanForm, backupPlanId) => {
    return Boolean(deployPlanForm?.selectedBackups.includes(backupPlanId));
  }
);

export const selectIsDeployPlanPreviewed = createAppSelector(
  [
    selectDeployForm,
    (_, __, preview: { planId: PlanId; period: Period }) => preview,
  ],
  (deployPlanForm, preview) => {
    return (
      deployPlanForm?.preview?.planId === preview.planId &&
      deployPlanForm?.preview?.period === preview.period
    );
  }
);

export const selectIsBackupPlanPreviewed = createAppSelector(
  [
    selectDeployForm,
    (_, __, planIds: PlanId[]) => planIds,
    (_, __, ___, period: Period) => period,
  ],
  (deployPlanForm, planIds, period) => {
    return (
      deployPlanForm?.preview?.planId &&
      planIds.includes(deployPlanForm.preview.planId) &&
      deployPlanForm?.preview?.period === period
    );
  }
);

export const selectDeployPlanPreview = createAppSelector(
  [selectDeployPlanForm],
  (deployPlanForm) =>
    deployPlanForm?.type === "deploy_form" ? deployPlanForm?.preview : undefined
);
export const selectPreviewIsBackup = createAppSelector(
  [selectDeployPlanForm],
  (deployPlanForm) => deployPlanForm?.type !== "deploy_form"
);

export const selectDeployPlanFormChecked = createAppSelector(
  [selectDeployForm],
  (deployPlanForm): { planId: PlanId; period: Period }[] => {
    if (deployPlanForm) {
      return [
        ...deployPlanForm.selectedPeriods.map((period) => ({
          planId: deployPlanForm.planId,
          period,
        })),
        ...deployPlanForm.selectedBackups.map((planId) => ({
          planId,
          period: minimumPeriod,
        })),
      ];
    } else {
      return [];
    }
  }
);

export const selectDeployPlanFormCheckedCount = createAppSelector(
  [selectDeployPlanFormChecked],
  (checked) => {
    return checked.length;
  }
);

export const selectPeriodMessages = createAppSelector(
  [selectMessagePlanForm],
  (SMSForm) => {
    return SMSForm?.periodMessage;
  }
);

export const selectPeriodMessagedIds = createAppSelector(
  [selectPeriodMessages],
  (periodMessages) => {
    return periodMessages ? Object.keys(periodMessages) : [];
  }
);

export const selectBackupPlanMessages = createAppSelector(
  [selectMessagePlanForm],
  (SMSForm) => {
    return SMSForm?.backupPlanMessage;
  }
);

export const selectBackupPlanMessagedIds = createAppSelector(
  [selectBackupPlanMessages],
  (backupMessages) => {
    return backupMessages ? Object.keys(backupMessages) : [];
  }
);

export const selectDeployFormSelectedPeriods = createAppSelector(
  [selectDeployForm],
  (deployForm) => deployForm?.selectedPeriods ?? []
);

export const selectDeployFormSelectedBackupPlanIds = createAppSelector(
  [selectDeployForm],
  (deployForm) => deployForm?.selectedBackups ?? []
);

export const selectDeployPlanFormIsSubmitting = createAppSelector(
  [selectDeployForm],
  (deployForm) => deployForm?.state === "submitting"
);

export const selectDeployFormManualHeatOverrides = createAppSelector(
  [selectDeployForm],
  (deployForm): Record<Period, Record<number, string>> =>
    deployForm?.manualHeatOverrides ?? {}
);

export const selectDeployFormMixOverride = createAppSelector(
  [
    selectDeployForm,
    (_, __, period: Period) => period,
    (_, __, ___, mixId: number) => mixId,
  ],
  (deployForm, period, mixId) =>
    deployForm?.manualHeatOverrides[period]?.[mixId] ?? ""
);
